import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';

const STATUS_LABEL = {
  calibrado: 'Calibrado',
  a_vencer: 'A Vencer',
  vencido: 'Vencido',
  aguardando_laudo: 'Aguardando Laudo',
  em_calibracao: 'Em Calibração',
  nao_calibrado: 'Não Calibrado',
  bloqueado: 'Bloqueado',
};

const STATUS_COLOR = {
  calibrado: 'bg-green-100 text-green-800',
  a_vencer: 'bg-yellow-100 text-yellow-800',
  vencido: 'bg-red-100 text-red-800',
  aguardando_laudo: 'bg-blue-100 text-blue-800',
  em_calibracao: 'bg-purple-100 text-purple-800',
  nao_calibrado: 'bg-slate-100 text-slate-600',
  bloqueado: 'bg-red-200 text-red-900',
};

const RESULTADO_BADGE = {
  ok: 'bg-green-100 text-green-800',
  nok: 'bg-red-100 text-red-800',
  parcial: 'bg-yellow-100 text-yellow-800',
};

const RESULTADO_LABEL = { ok: 'OK', nok: 'NOK', parcial: 'Parcial' };

const LINE_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

function buildGraficoData(verificacoes) {
  if (!verificacoes.length) return { series: [], data: [] };

  const maxPontos = Math.max(...verificacoes.map(v => v.pontos.length));
  const series = Array.from({ length: maxPontos }, (_, i) => ({
    key: `p${i}`,
    label: `Ponto ${i + 1}`,
    emp: null,
    empTipo: 'absoluto',
  }));

  verificacoes.forEach(v => {
    v.pontos.forEach((p, i) => {
      if (i < series.length && p.empValor != null && series[i].emp == null) {
        series[i].emp = p.empValor;
        series[i].empTipo = p.empTipo || 'absoluto';
        series[i].aplicado = p.aplicado;
      }
    });
  });

  const data = verificacoes.map(v => {
    const row = { data: new Date(v.data).toLocaleDateString('pt-BR') };
    v.pontos.forEach((p, i) => {
      if (i < series.length) row[`p${i}`] = p.erro;
    });
    return row;
  });

  return { series, data };
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.value != null ? p.value.toFixed(4) : '—'}
        </p>
      ))}
    </div>
  );
}

function GraficoVerificacoes({ instrumentId }) {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/verificacoes/instrumento/${instrumentId}`, { params: { limite: 90 } })
      .then(r => setHistorico(r.data))
      .catch(() => setHistorico([]))
      .finally(() => setLoading(false));
  }, [instrumentId]);

  if (loading) return <p className="text-sm text-slate-400 py-4">Carregando gráfico...</p>;
  if (!historico.length) return <p className="text-sm text-slate-400 py-4">Nenhuma verificação registrada para este instrumento.</p>;

  const { series, data } = buildGraficoData(historico);

  return (
    <div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="data" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 2" />
          {series.map((s, i) => {
            const color = LINE_COLORS[i % LINE_COLORS.length];
            const empAbs = s.emp != null && s.empTipo === 'percentual'
              ? Math.abs(s.aplicado ?? 0) * (s.emp / 100)
              : s.emp;
            return [
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />,
              empAbs != null && (
                <ReferenceLine key={`emp+${i}`} y={empAbs} stroke={color} strokeDasharray="6 3" strokeOpacity={0.6} />
              ),
              empAbs != null && (
                <ReferenceLine key={`emp-${i}`} y={-empAbs} stroke={color} strokeDasharray="6 3" strokeOpacity={0.6} />
              ),
            ];
          })}
        </LineChart>
      </ResponsiveContainer>
      <p className="text-xs text-slate-400 mt-1">Linhas tracejadas = ± EMP por ponto</p>
    </div>
  );
}

export default function InstrumentDetailPage() {
  const { id } = useParams();
  const { formatarData } = useTenant();
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificacoes, setVerificacoes] = useState([]);
  const [loadingVerif, setLoadingVerif] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/instruments/${id}`)
      .then(r => setInstrument(r.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setLoadingVerif(true);
    api.get('/verificacoes', { params: { instrumentId: id, limite: 10 } })
      .then(r => setVerificacoes(r.data.dados || []))
      .catch(() => setVerificacoes([]))
      .finally(() => setLoadingVerif(false));
  }, [id]);

  if (loading) return <div className="p-8">Carregando...</div>;
  if (!instrument) return <div className="p-8 text-red-500">Instrumento não encontrado.</div>;

  const status = instrument.calibrationStatus || 'nao_calibrado';

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">{instrument.tag}</h2>
            <p className="text-slate-500">{instrument.description}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLOR[status] || 'bg-slate-100 text-slate-600'}`}>
            {STATUS_LABEL[status] || status}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
          <div><span className="text-slate-500">Setor:</span> {instrument.sector || '-'}</div>
          <div><span className="text-slate-500">Classe:</span> {instrument.instrumentClass?.name || '-'}</div>
          <div><span className="text-slate-500">Criticidade:</span> {instrument.criticity || '-'}</div>
          <div>
            <span className="text-slate-500">Faixa:</span>{' '}
            {instrument.measurementMin != null ? instrument.measurementMin : '-'} ~{' '}
            {instrument.measurementMax != null ? instrument.measurementMax : '-'}{' '}
            {instrument.measurementUnit || ''}
          </div>
          <div><span className="text-slate-500">Local Calib.:</span> {instrument.localCalibracao === 'in_loco' ? 'In Loco' : instrument.localCalibracao === 'laboratorio' ? 'Laboratório' : '-'}</div>
          <div><span className="text-slate-500">Frequência:</span> {instrument.calibrationFrequencyDays ? `${instrument.calibrationFrequencyDays} dias` : '-'}</div>
          <div><span className="text-slate-500">Próx. Calib.:</span> {formatarData(instrument.nextCalibrationDate)}</div>
          <div><span className="text-slate-500">Últ. Calib.:</span> {instrument.lastCalibrationDate ? new Date(instrument.lastCalibrationDate).toLocaleDateString('pt-BR') : '-'}</div>
        </div>
      </div>

      {/* Gráfico de Verificações em Campo */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Histórico de Verificações em Campo</h3>
        <GraficoVerificacoes instrumentId={id} />
      </div>

      {/* Últimas Verificações */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Últimas Verificações</h3>
        {loadingVerif ? (
          <p className="text-sm text-slate-400">Carregando...</p>
        ) : verificacoes.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhuma verificação registrada.</p>
        ) : (
          <div className="space-y-2">
            {verificacoes.map(v => (
              <div key={v._id} className="flex items-center gap-4 border-l-2 border-slate-200 pl-4 py-2 text-sm">
                <span className="text-slate-600 min-w-[90px]">{new Date(v.data).toLocaleDateString('pt-BR')}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RESULTADO_BADGE[v.resultado] || 'bg-slate-100 text-slate-600'}`}>
                  {RESULTADO_LABEL[v.resultado] || v.resultado}
                </span>
                <span className="text-slate-500 text-xs">{v.pontos.length} ponto(s)</span>
                {v.observacao && <span className="text-slate-400 text-xs truncate">{v.observacao}</span>}
                <span className="ml-auto text-slate-400 text-xs">{v.responsavelNome}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
